import React, { Suspense, useEffect, useRef, useState } from "react";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { useProgress, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import "../styles/DesktopRenderer.css";

// Komponent ładowania
function LoadingIndicator() {
  const { progress } = useProgress();
  const [isLoading, setIsLoading] = useState(true);

  // Dodajemy efekt, który zatrzyma animację po załadowaniu
  useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [progress]);

  return (
    <Html center>
      <div
        className="loader-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "50px",
          marginTop: "-35vh",
          backgroundColor: "rgba(0,0,0,0.7)",
          padding: "30px",
          borderRadius: "10px",
        }}
      >
        <div className="loading-text">Ładowanie wnętrza</div>
        <div
          className="percentage"
          style={{ fontSize: "1.2em", fontWeight: "bold" }}
        >
          {Math.round(progress)}%
        </div>
        <div className={`funny-loader ${!isLoading ? "loaded" : ""}`}>
          <div className="inner-circle"></div>
        </div>
      </div>
    </Html>
  );
}

// Komponent modelu 3D - wykrywa ściany na potrzeby kolizji
const Model = ({ setWalls }) => {
  const gltf = useLoader(GLTFLoader, "/models/showcase_room.glb");
  const modelRef = useRef();
  const walls = useRef([]);
  const [lightsConfigured, setLightsConfigured] = useState(false);
  const originalIntensities = useRef(new Map());

  useEffect(() => {
    if (gltf && !lightsConfigured) {
      const wallObjects = [];

      // Najpierw znajdź obiekt 'walls' (rodzic)
      let wallsParent = null;
      gltf.scene.traverse((node) => {
        if (node.name && node.name.toLowerCase() === "walls") {
          wallsParent = node;
          console.log("Znaleziono obiekt-rodzic 'walls':", wallsParent);
        }
      });

      // Przeszukaj model w poszukiwaniu ścian i skonfiguruj cienie
      gltf.scene.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;

          // Identyfikuj ściany - sprawdź czy jest dzieckiem obiektu 'walls'
          // LUB ma w nazwie słowo wall/ściana/itp.
          if (
            (wallsParent &&
              node.parent &&
              (node.parent === wallsParent ||
                node.parent.parent === wallsParent)) ||
            /wall/i.test(node.name) ||
            node.name.toLowerCase().includes("sciana") ||
            node.name.toLowerCase().includes("ściana") ||
            node.name.toLowerCase().includes("col") ||
            node.name.toLowerCase().includes("floor") ||
            node.name.toLowerCase().includes("ceiling") ||
            node.name.toLowerCase().includes("sufit") ||
            node.name.toLowerCase().includes("podloga") ||
            node.name.toLowerCase().includes("podłoga")
          ) {
            console.log(
              "Dodaję ścianę do kolizji:",
              node.name,
              "rodzic:",
              node.parent ? node.parent.name : "brak",
            );
            node.geometry.computeBoundingBox();
            const box = node.geometry.boundingBox.clone();
            box.applyMatrix4(node.matrixWorld);

            wallObjects.push({
              name: node.name,
              box: box,
              position: node.position.clone(),
              quaternion: node.quaternion.clone(),
              scale: node.scale.clone(),
              mesh: node,
            });
          }
        }

        if (node.isLight) {
          if (!originalIntensities.current.has(node.uuid)) {
            originalIntensities.current.set(node.uuid, node.intensity);
          }

          const originalIntensity = originalIntensities.current.get(node.uuid);
          node.intensity = originalIntensity * 0.005;
        }
      });

      walls.current = wallObjects;

      // Przekaż informacje o ścianach do komponentu nadrzędnego
      if (setWalls) setWalls(wallObjects);

      console.log("Wykryto ścian:", wallObjects.length);

      // Oznacz, że światła zostały już skonfigurowane
      setLightsConfigured(true);
    }
  }, [gltf, setWalls, lightsConfigured]);

  return <primitive object={gltf.scene} ref={modelRef} />;
};

// Sterowanie w pierwszej osobie - ruch WASD, rozglądanie się prawym przyciskiem myszy, teleport lewym
function FirstPersonControls({ walls = [] }) {
  const { camera, gl, scene, raycaster, pointer } = useThree();
  const [lockControls, setLockControls] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [teleporting, setTeleporting] = useState(false);
  const teleportTarget = useRef(null);
  const teleportStartTime = useRef(0);
  const teleportDuration = 1500; // ms

  // Zmniejsz prędkość poruszania się dla lepszej kontroli
  const moveSpeed = useRef(3.0);
  const keys = useRef({});
  const mouseSensitivity = useRef(0.002);
  const time = useRef(Date.now());
  const playerRadius = 0.3;
  const euler = useRef(new THREE.Euler(0, 0, 0, "YXZ"));
  const currentVelocity = useRef(new THREE.Vector3(0, 0, 0));
  const lastValidPosition = useRef(camera.position.clone());

  // Inicjalizacja pozycji kamery - wnętrze pokoju, na wysokości oczu, patrząc w stronę kominka i mebli
  useEffect(() => {
    camera.position.set(0, 1.65, 2.0);
    camera.rotation.set(0, 0, 0);
    euler.current.set(0, 0, 0);
    lastValidPosition.current.copy(camera.position);
  }, [camera]);

  // Obsługa klawiszy
  useEffect(() => {
    const handleKeyDown = (e) => {
      keys.current[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e) => {
      keys.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Obsługa myszy i blokady kursora
  useEffect(() => {
    const canvas = gl.domElement;

    // Zapobiegaj standardowemu menu kontekstowemu przeglądarki
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleMouseDown = (e) => {
      // Zmiana na prawy przycisk myszy (button === 2)
      if (e.button === 2) {
        setMouseDown(true);

        try {
          // W nowszych przeglądarkach używamy nowych opcji API PointerLock
          if (canvas.requestPointerLock) {
            // Sprawdź czy przeglądarka obsługuje suppressBanner (opcja niestandardowa Chrome)
            if (
              typeof Element.prototype.requestPointerLock === "function" &&
              "pointerLockOptions" in Element.prototype.requestPointerLock
            ) {
              canvas.requestPointerLock({
                unadjustedMovement: true,
                suppressBanner: true, // Ta opcja ukrywa baner "Naciśnij ESC..."
              });
            } else {
              // Standardowe API bez dodatkowych opcji
              canvas.requestPointerLock();
            }
          }
        } catch (error) {
          console.log("Błąd podczas próby zablokowania kursora:", error);
          canvas.requestPointerLock(); // Fallback do standardowego API
        }
      }
    };

    const handleMouseUp = (e) => {
      // Zmiana na prawy przycisk myszy (button === 2)
      if (e.button === 2) {
        setMouseDown(false);
        if (document.pointerLockElement === canvas) {
          document.exitPointerLock();
        }
      }
    };

    const handlePointerLockChange = () => {
      if (document.pointerLockElement === canvas) {
        setLockControls(true);
        document.body.style.cursor = "none";

        // Pokaż własny komunikat zamiast systemowego
        const customMessage = document.getElementById(
          "custom-pointer-lock-message",
        );
        if (customMessage) {
          customMessage.style.display = "block";
        }

        // Dodaj element, który pomoże ukryć komunikat systemowy
        if (!document.querySelector(".pointer-lock-hide-message")) {
          const hideElement = document.createElement("div");
          hideElement.className = "pointer-lock-hide-message";
          document.body.appendChild(hideElement);
        }
      } else {
        setLockControls(false);
        document.body.style.cursor = "auto";
        setMouseDown(false);

        // Ukryj własny komunikat
        const customMessage = document.getElementById(
          "custom-pointer-lock-message",
        );
        if (customMessage) {
          customMessage.style.display = "none";
        }

        // Usuń element ukrywający komunikat systemowy
        const hideElement = document.querySelector(
          ".pointer-lock-hide-message",
        );
        if (hideElement) {
          hideElement.remove();
        }
      }
    };

    const handleMouseMove = (e) => {
      if (!lockControls || !mouseDown) return;

      const movementX = e.movementX || e.mozMovementX || 0;
      const movementY = e.movementY || e.mozMovementY || 0;

      euler.current.y -= movementX * mouseSensitivity.current;
      euler.current.x -= movementY * mouseSensitivity.current;
      euler.current.x = Math.max(
        -Math.PI / 2 + 0.1,
        Math.min(Math.PI / 2 - 0.1, euler.current.x),
      );
      camera.quaternion.setFromEuler(euler.current);
    };

    // Dodaj nowy event listener dla contextmenu
    canvas.addEventListener("contextmenu", handleContextMenu);
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("pointerlockchange", handlePointerLockChange);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Usuń listener dla contextmenu
      canvas.removeEventListener("contextmenu", handleContextMenu);
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener(
        "pointerlockchange",
        handlePointerLockChange,
      );
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [gl, lockControls, mouseDown, camera]);

  // Dodaj obsługę kliknięcia lewym przyciskiem myszy dla teleportacji
  useEffect(() => {
    const handleLeftClick = (e) => {
      if (e.button !== 0 || teleporting) return;
      raycaster.setFromCamera(pointer, camera);
      const floorObjects = [];
      scene.traverse((object) => {
        if (
          object.isMesh &&
          (object.name.toLowerCase().includes("floor") ||
            object.name.toLowerCase().includes("ground"))
        ) {
          floorObjects.push(object);
        }
      });

      // Sprawdź przecięcie z podłogą
      const intersects = raycaster.intersectObjects(floorObjects);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        const currentHeight = camera.position.y;
        teleportTarget.current = {
          position: new THREE.Vector3(point.x, currentHeight, point.z),
        };
        setTeleporting(true);
        teleportStartTime.current = Date.now();
      }
    };

    gl.domElement.addEventListener("mousedown", handleLeftClick);

    return () => {
      gl.domElement.removeEventListener("mousedown", handleLeftClick);
    };
  }, [raycaster, pointer, camera, gl, scene, teleporting]);

  // Logika ruchu
  useFrame(() => {
    const currentTime = Date.now();
    const delta = Math.min((currentTime - time.current) / 1000, 0.1);
    time.current = currentTime;

    // Kierunek ruchu na podstawie wciśniętych klawiszy
    const targetVelocity = new THREE.Vector3(0, 0, 0);
    const forward = keys.current["w"] || keys.current["arrowup"] || false;
    const backward = keys.current["s"] || keys.current["arrowdown"] || false;
    const left = keys.current["a"] || keys.current["arrowleft"] || false;
    const right = keys.current["d"] || keys.current["arrowright"] || false;

    if (forward) targetVelocity.z -= 1;
    if (backward) targetVelocity.z += 1;
    if (left) targetVelocity.x -= 1;
    if (right) targetVelocity.x += 1;

    if (targetVelocity.length() > 0) targetVelocity.normalize();

    const worldForward = new THREE.Vector3(0, 0, -1).applyQuaternion(
      camera.quaternion,
    );
    worldForward.y = 0;
    worldForward.normalize();

    const worldRight = new THREE.Vector3(1, 0, 0).applyQuaternion(
      camera.quaternion,
    );
    worldRight.y = 0;
    worldRight.normalize();

    if (targetVelocity.length() > 0) {
      currentVelocity.current.set(0, 0, 0);

      if (targetVelocity.z !== 0) {
        currentVelocity.current.add(
          worldForward
            .clone()
            .multiplyScalar(-targetVelocity.z * moveSpeed.current),
        );
      }
      if (targetVelocity.x !== 0) {
        currentVelocity.current.add(
          worldRight
            .clone()
            .multiplyScalar(targetVelocity.x * moveSpeed.current),
        );
      }
    } else {
      currentVelocity.current.set(0, 0, 0);
    }

    // Zastosuj ruch z obsługą kolizji
    if (currentVelocity.current.length() > 0) {
      const moveVector = currentVelocity.current.clone().multiplyScalar(delta);
      const targetPosition = camera.position.clone().add(moveVector);

      // Sprawdź kolizje
      if (!checkCollision(camera.position, targetPosition, walls, [], playerRadius)) {
        camera.position.copy(targetPosition);
        lastValidPosition.current.copy(camera.position);
      } else {
        // W przypadku kolizji, spróbuj przesunąć się tylko w kierunku X lub Z
        const xMove = new THREE.Vector3(moveVector.x, 0, 0);
        const zMove = new THREE.Vector3(0, 0, moveVector.z);

        let canMoveX = false;
        let canMoveZ = false;

        if (xMove.length() > 0) {
          const xTarget = camera.position.clone().add(xMove);
          canMoveX = !checkCollision(camera.position, xTarget, walls, [], playerRadius);
        }

        if (zMove.length() > 0) {
          const zTarget = camera.position.clone().add(zMove);
          canMoveZ = !checkCollision(camera.position, zTarget, walls, [], playerRadius);
        }

        if (canMoveX) {
          camera.position.x += xMove.x;
        }
        if (canMoveZ) {
          camera.position.z += zMove.z;
        }

        if (canMoveX || canMoveZ) {
          lastValidPosition.current.copy(camera.position);
        } else {
          // Jeśli nie można się poruszyć w żadnym kierunku, wróć do ostatniej ważnej pozycji
          camera.position.copy(lastValidPosition.current);
        }

        currentVelocity.current.set(0, 0, 0);
      }
    }

    // Obsługa teleportacji
    if (teleporting && teleportTarget.current) {
      const now = Date.now();
      const elapsed = now - teleportStartTime.current;
      const progress = Math.min(elapsed / teleportDuration, 1);

      // Użyj krzywej wygładzania - szybsze w środku, bardziej naturalne na początku i końcu
      const easeProgress =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Interpoluj pozycję
      const startPos = camera.position.clone();
      const targetPos = teleportTarget.current.position;

      // Prosta interpolacja liniowa z zachowaniem wysokości
      const newPos = new THREE.Vector3(
        startPos.x + (targetPos.x - startPos.x) * easeProgress,
        startPos.y, // Zachowaj stałą wysokość Y
        startPos.z + (targetPos.z - startPos.z) * easeProgress,
      );

      // Ustaw nową pozycję
      camera.position.copy(newPos);

      // Odblokuj kontrolę już przy 70% animacji
      if (progress >= 0.7) {
        setTeleporting(false);

        if (progress < 1) {
          // Ustaw końcową pozycję jeśli to ostatni krok
          if (progress > 0.95) {
            camera.position.set(targetPos.x, startPos.y, targetPos.z);
          }
        }
      }
    }
  });

  return null;
}

// Funkcja sprawdzająca kolizję między punktami i ścianami
function checkCollision(start, end, walls, doors = [], radius = 0.3) {
  // Tablice obiektów kolizyjnych
  const wallMeshes = walls.filter((w) => w.mesh).map((w) => w.mesh);
  const doorMeshes = doors.filter((d) => d.mesh).map((d) => d.mesh);
  const allColliders = [...wallMeshes, ...doorMeshes];

  // Jeśli nie ma obiektów kolizyjnych, loguj błąd
  if (allColliders.length === 0) {
    console.warn("checkCollision: Brak obiektów kolizyjnych!");
    return false;
  }

  // Kierunek i odległość ruchu
  const moveVec = new THREE.Vector3().subVectors(end, start);
  const direction = moveVec.clone().normalize();
  const distance = moveVec.length();

  // Podstawowy promień sprawdzania kolizji
  const raycaster = new THREE.Raycaster(start, direction, 0, distance + radius);
  const hits = raycaster.intersectObjects(allColliders, true);

  if (hits.length > 0) {
    return true;
  }

  // Dodatkowe promienie dla lepszej detekcji kolizji
  const sideAngles = [
    Math.PI / 4,
    Math.PI / 2,
    (3 * Math.PI) / 4,
    Math.PI,
    (5 * Math.PI) / 4,
    (3 * Math.PI) / 2,
    (7 * Math.PI) / 4,
  ];

  for (const angle of sideAngles) {
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    const offsetStart = start.clone().add(new THREE.Vector3(x, 0, z));

    const sideRaycaster = new THREE.Raycaster(
      offsetStart,
      direction,
      0,
      distance,
    );
    const sideHits = sideRaycaster.intersectObjects(allColliders, true);

    if (sideHits.length > 0 && sideHits[0].distance <= distance) {
      return true;
    }
  }

  return false;
}

// Główny komponent renderera
const DesktopRenderer = () => {
  const [walls, setWalls] = useState([]);

  // Ukryj overlay ładowania po pewnym czasie
  useEffect(() => {
    const timer = setTimeout(() => {
      const loadingOverlay = document.getElementById("loading-overlay");
      if (loadingOverlay) {
        loadingOverlay.style.opacity = 0;
        setTimeout(() => {
          loadingOverlay.style.display = "none";
        }, 500);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Funkcja wywołana po utworzeniu canvasa
  const handleCreated = () => {
    setTimeout(() => {
      const loadingOverlay = document.getElementById("loading-overlay");
      if (loadingOverlay) {
        loadingOverlay.style.opacity = 0;
        setTimeout(() => {
          loadingOverlay.style.display = "none";
        }, 500);
      }
    }, 1000);
  };

  return (
    <div className="renderer-container">
      <Canvas
        camera={{
          position: [0, 1.65, 2.0],
          rotation: [0, 0, 0],
          fov: 75,
        }}
        style={{ width: "100%", height: "100%" }}
        onCreated={handleCreated}
      >
        <color attach="background" args={["#cfc9c0"]} />
        {/* Model nie zawiera własnych świateł, więc oświetlenie sceny ustawiamy ręcznie */}
        <ambientLight intensity={0.8} color="#fff6e6" />

        <hemisphereLight
          skyColor="#fff0dd"
          groundColor="#e0e0ff"
          intensity={0.6}
          position={[0, 50, 0]}
        />

        <directionalLight
          position={[3, 4.5, -3]}
          intensity={1.2}
          color="#fff2df"
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={0.5}
          shadow-camera-far={20}
          shadow-camera-left={-6}
          shadow-camera-right={6}
          shadow-camera-top={6}
          shadow-camera-bottom={-6}
        />

        {/* Światło uzupełniające z przeciwnej strony, by druga strona pokoju nie była czarna */}
        <directionalLight position={[-3, 3, 4]} intensity={0.7} color="#e7ecff" />

        <Suspense fallback={<LoadingIndicator />}>
          <Model setWalls={setWalls} />
          <FirstPersonControls walls={walls} />
        </Suspense>
      </Canvas>

      <div id="loading-overlay" className="loading-overlay"></div>

      {/* Panel z opisem sterowania */}
      <div className="interface-container">
        <div className="controls-info">
          WASD - poruszanie | Prawy przycisk - rozglądanie | Lewy przycisk -
          teleport
        </div>
      </div>
    </div>
  );
};

export default DesktopRenderer;
